class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    if (!this.input || !this.form) return;

    this.form.addEventListener('reset', this.onFormReset.bind(this));
    this.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener(
      'input',
      debounce(() => this.onChange(), 300)
    );
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('keydown', this.onInputKeydown.bind(this));

    // Close when the shopper clicks anywhere outside this component. Without
    // this the panel stayed open until Escape or an empty field, which on the
    // desktop bar meant it hung over the page while you scrolled.
    this.onDocumentPointerDown = (event) => {
      if (!this.isOpen) return;
      if (this.contains(event.target)) return;
      this.close();
    };
    document.addEventListener('pointerdown', this.onDocumentPointerDown);

    if (this.input.value !== '') {
      this.onFocus();
    }
  }

  disconnectedCallback() {
    // The theme editor tears sections down and rebuilds them, so the document
    // level listener has to come off with the element or they accumulate.
    if (this.onDocumentPointerDown) {
      document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    }
    this.abortController?.abort();
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    this.toggleResetButton();

    // 31 August 2026. THE PANEL NO LONGER EMPTIES ITSELF ON EVERY KEYSTROKE.
    //
    // This used to remove #predictive-search-results-groups-wrapper whenever
    // the new term was not a continuation of the old one, which is exactly
    // what deleting a character produces. The results vanished immediately and
    // the replacements could not arrive for at least the 300ms debounce plus a
    // round trip, so backspacing read as the dropdown going blank and hanging.
    //
    // Nothing is torn down here now. The old results stay on screen until the
    // new ones are ready to replace them, and getSearchResults below serves
    // from cache first, so backspacing through a term you have already typed is
    // instant and makes no request at all.
    const newSearchTerm = this.getQuery();
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) event.preventDefault();
  }

  onFormReset(event) {
    event.preventDefault();

    this.input.value = '';
    this.input.focus();
    this.toggleResetButton();
    this.searchTerm = '';
    this.abortController.abort();
    this.abortController = new AbortController();
    this.closeResults(true);
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onInputKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close(true);
    }
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();

    // Drop any request still in flight. Without this a slow early keystroke
    // could land after a fast later one and put stale results on screen.
    this.abortController.abort();
    this.abortController = new AbortController();

    // Cache first, and BEFORE the loading state is set, so a cached term
    // renders with no blank frame at all. Deleting characters walks back
    // through terms that are already cached, which is the common case.
    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    this.setLiveRegionLoadingState();

    // 31 August 2026. PRODUCTS ONLY, AND WHY THE PARAMETERS ARE HERE.
    //
    // The call used to be q + section_id and nothing else, so the endpoint
    // applied its defaults and returned query suggestions, collections, pages
    // and articles alongside the products. On this store that put blog posts
    // and policy pages in front of shoppers who were typing a bottle name.
    //
    // resources[type]=product narrows it. sections/predictive-search.liquid is
    // unchanged in that respect: its collections, pages and articles groups
    // simply render nothing because those arrays come back empty.
    //
    // unavailable_products=last keeps sold-out bottles in the list but sends
    // them to the bottom, which suits a catalogue where lines come back.
    //
    // The brackets are deliberately NOT encoded. That is the form Shopify's own
    // reference implementation uses and the endpoint expects.
    const searchUrl =
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}` +
      `&resources[type]=product` +
      `&resources[limit]=6` +
      `&resources[options][unavailable_products]=last` +
      `&section_id=predictive-search`;

    fetch(searchUrl, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const section = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');

        if (!section) return;

        const resultsMarkup = section.innerHTML;

        // Queried live rather than cached in the constructor, because the two
        // instances on this page are upgraded at different moments and the one
        // that runs first would otherwise never see the other.
        document
          .querySelectorAll('predictive-search')
          .forEach((instance) => {
            if (instance.cachedResults) {
              instance.cachedResults[queryKey] = resultsMarkup;
            }
          });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        // AbortError, which is every superseded keystroke. Expected, not a fault.
        if (error?.name === 'AbortError' || error?.code === 20) {
          return;
        }
        // Rethrowing here produced an unhandled promise rejection on every
        // network blip. Close quietly and log instead.
        this.close();
        console.error('Predictive search request failed', error);
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText =
      this.loadingText || this.getAttribute('data-loading-text');

    if (!this.predictiveSearchResults) return;

    this.predictiveSearchResults.removeAttribute('hidden');

    // Only clear the panel when there is nothing worth keeping in it. Clearing
    // it unconditionally is what made an in-flight search look like a failure.
    if (this.getAttribute('results') !== 'true') {
      this.restoreLoadingMarkup();
    }

    this.predictiveSearchResults.setAttribute('aria-busy', 'true');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement || !statusText) return;
    this.statusElement.textContent = statusText;
  }

  refreshParentFocusTrap() {
    // Fully optional. This is a theme global that only exists once global.js
    // has run, and an unguarded read threw on any page where it had not.
    window.Woolman?.Utils?.refreshTrapFocus?.();
  }

  restoreLoadingMarkup() {
    this.predictiveSearchResults.innerHTML = `
      <div class="predictive-search__loading-state">
        <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
          <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
        </svg>
      </div>
    `;
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
    this.refreshParentFocusTrap();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.predictiveSearchResults.removeAttribute('aria-busy');
    const countElement = this.querySelector(
      '[data-predictive-search-live-region-count-value]'
    );
    if (countElement) {
      this.setLiveRegionText(countElement.textContent);
    }
  }

  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', 'true');
    this.predictiveSearchResults.removeAttribute('hidden');
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
      this.restoreLoadingMarkup();
    }

    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    this.predictiveSearchResults.removeAttribute('aria-busy');
    this.predictiveSearchResults.setAttribute('hidden', '');
    this.predictiveSearchResults.removeAttribute('style');
    this.refreshParentFocusTrap();
  }

  toggleResetButton() {
    // Guard added 31 August 2026. This runs on every keystroke and used to
    // assume the reset button exists; a <predictive-search> without one threw
    // a TypeError here and took the whole component down silently.
    if (!this.resetButton) return;

    const resetIsHidden = this.resetButton.classList.contains('hidden');

    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
